/** @format */

import { FC, useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, ScanSearch, ArrowLeft } from "lucide-react";
import Header from "../../components/ui/Header";
import { rmaService, RMAPendingReviewItem } from "../../api/rmaService";
import { useOrgRole } from "../../hooks/useOrgRole";

function formatWhen(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso.endsWith("Z") ? iso : `${iso}Z`);
  return d.toLocaleString();
}

const RMAPendingReviewPage: FC = () => {
  const navigate = useNavigate();
  const { canPublishRMA, isRoleResolving } = useOrgRole();
  const [items, setItems] = useState<RMAPendingReviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await rmaService.getPendingReview({ page: 1, limit: 50 });
      setItems(res.analyses);
      setTotal(res.total);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canPublishRMA) load();
  }, [canPublishRMA, load]);

  if (isRoleResolving) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-950 to-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  if (!canPublishRMA) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-950 to-black text-white">
      <Header />
      <main className="pt-20 px-4 pb-16 max-w-4xl mx-auto">
        <button
          type="button"
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-slate-400 hover:text-white text-sm mt-8 mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center">
            <ScanSearch className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">RMA — pending review</h1>
            <p className="text-slate-400 text-sm">
              Analyses waiting for you to edit and publish to the organization.
            </p>
          </div>
        </div>
        <p className="text-slate-500 text-sm mb-6">{total} total</p>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <p className="text-slate-500 text-center py-12">No pending analyses.</p>
        ) : (
          <ul className="space-y-2">
            {items.map((row) => (
              <li key={row.analysis_id}>
                <button
                  type="button"
                  onClick={() => navigate(`/sop/${row.sop_id}/rma`)}
                  className="w-full text-left rounded-xl border border-slate-700/50 bg-slate-800/30 hover:bg-slate-800/50 px-4 py-3 transition-colors"
                >
                  <div className="flex justify-between gap-4 flex-wrap">
                    <span className="font-medium text-white">{row.sop_title ?? row.sop_id}</span>
                    <span className="text-slate-400 text-sm">{row.organization_name ?? "—"}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {formatWhen(row.created_at)} · {row.passed} pass / {row.failed} fail · {row.total}{" "}
                    images
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
};

export default RMAPendingReviewPage;
